import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { GatewayChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';
import { LocationFilterStateController } from '@app/feature/location/location-filter-state/location-filter-state.controller';
import { NetworkFilterStateController } from '@app/feature/network/network-filter-state/network-filter-state.controller';
import { TariffFilterStateController } from '@app/feature/tariff/tariff-filter-state/tariff-filter-state.controller';

import { ChargePointChannelOcppStatusFilterStateController } from '../charge-point-channel-ocpp-status-filter-state/charge-point-channel-ocpp-status-filter-state.controller';
import { ChargePointHasLocationFilterStateController } from '../charge-point-has-location-filter-state/charge-point-has-location-filter-state.controller';
import { ChargePointHasSubscriptionFilterStateController } from '../charge-point-has-subscription-filter-state/charge-point-has-subscription-filter-state.controller';
import { ChargePointHasTariffFilterStateController } from '../charge-point-has-tariff-filter-state/charge-point-has-tariff-filter-state.controller';
import { ChargePointIsMaintenanceFilterStateController } from '../charge-point-is-maintenance-filter-state/charge-point-is-maintenance-filter-state.controller';
import { ChargePointIsOnlineFilterStateController } from '../charge-point-is-online-filter-state/charge-point-is-online-filter-state.controller';
import { ChargePointListLastMessageReceivedDateFilterStateController } from '../charge-point-list-last-message-received-date-filter-state/charge-point-list-last-message-received-date-filter-state.controller';
import { ChargePointListChannelsColumn } from './charge-point-list-channels.column';
import { ChargePointListCreatedAtColumn } from './charge-point-list-created-at.column';
import { ChargePointListCustomerColumn } from './charge-point-list-customer.column';
import { ChargePointListIdentityColumn } from './charge-point-list-identity.column';
import { ChargePointListIsLockedColumn } from './charge-point-list-is-locked.column';
import { ChargePointListIsOnlineColumn } from './charge-point-list-is-online.column';
import { ChargePointListLastRecordedActivityColumn } from './charge-point-list-last-recorded-activity.column';
import { ChargePointListLocationCityColumn } from './charge-point-list-location-city.column';
import { ChargePointListLocationColumn } from './charge-point-list-location.column';
import { ChargePointListModelColumn } from './charge-point-list-model.column';
import { ChargePointListNameColumn } from './charge-point-list-name.column';
import { ChargePointListNetworkColumn } from './charge-point-list-network.column';
import { ChargePointListTariffColumn } from './charge-point-list-tariff.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const CHARGE_POINT_LIST_SORT_FIELD: NonNullable<
  GetChargePointsPaginatedModel['sortField']
> = 'createdAt';

export const CHARGE_POINT_LIST_SORT_ORDER: NonNullable<
  GetChargePointsPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetChargePointsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const CHARGE_POINT_LIST_FILTER: ResourceListFilter = {
  isOnline: {
    componentType: ChargePointIsOnlineFilterStateController,
  },
  isMaintenance: {
    componentType: ChargePointIsMaintenanceFilterStateController,
  },
  channelOcppStatus: {
    componentType: ChargePointChannelOcppStatusFilterStateController,
  },
  customerId: {
    componentType: CustomerFilterStateController,
  },
  hasTariff: {
    componentType: ChargePointHasTariffFilterStateController,
  },
  hasLocation: {
    componentType: ChargePointHasLocationFilterStateController,
  },
  hasSubscription: {
    componentType: ChargePointHasSubscriptionFilterStateController,
  },
  lastMessageReceivedDate: {
    componentType: ChargePointListLastMessageReceivedDateFilterStateController,
  },
  locationId: {
    componentType: LocationFilterStateController,
  },
  networkId: {
    componentType: NetworkFilterStateController,
  },
  tariffId: {
    componentType: TariffFilterStateController,
  },
} as const;

export const CHARGE_POINT_LIST_FILTER_NAME_LIST = Object.keys(
  CHARGE_POINT_LIST_FILTER,
) as Array<keyof typeof CHARGE_POINT_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const chargePointListColumnHelper =
  createColumnHelper<GatewayChargePointModel>();

type ResourceColumnType = ColumnDef<GatewayChargePointModel, any>[];

export const CHARGE_POINT_LIST_COLUMN_LIST: ResourceColumnType = [
  chargePointListColumnHelper.accessor(
    'isOnline',
    ChargePointListIsOnlineColumn,
  ),
  chargePointListColumnHelper.accessor(
    'identity',
    ChargePointListIdentityColumn,
  ),
  chargePointListColumnHelper.accessor('name', ChargePointListNameColumn),
  chargePointListColumnHelper.accessor(
    'location',
    ChargePointListLocationColumn,
  ),
  chargePointListColumnHelper.accessor(
    'location',
    ChargePointListLocationCityColumn,
  ),
  chargePointListColumnHelper.accessor('model', ChargePointListModelColumn),
  chargePointListColumnHelper.accessor('network', ChargePointListNetworkColumn),
  chargePointListColumnHelper.accessor(
    'customer',
    ChargePointListCustomerColumn,
  ),
  chargePointListColumnHelper.accessor('tariff', ChargePointListTariffColumn),
  chargePointListColumnHelper.accessor(
    'channels',
    ChargePointListChannelsColumn,
  ),
  chargePointListColumnHelper.accessor(
    'isLocked',
    ChargePointListIsLockedColumn,
  ),
  chargePointListColumnHelper.accessor(
    'createdAt',
    ChargePointListCreatedAtColumn,
  ),
  chargePointListColumnHelper.accessor(
    'lastRecordedActivityDate',
    ChargePointListLastRecordedActivityColumn,
  ),
];

export const CHARGE_POINT_LIST_COLUMN_ID_LIST =
  CHARGE_POINT_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const CHARGE_POINT_LIST_COLUMN_ORDER = [
  ...CHARGE_POINT_LIST_COLUMN_ID_LIST,
];

export const CHARGE_POINT_LIST_COLUMN_VISIBILITY: VisibilityState =
  CHARGE_POINT_LIST_COLUMN_ID_LIST.reduce(
    (columnVisibility, columnId) => ({
      ...columnVisibility,
      [columnId]: true,
    }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface ChargePointListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const CHARGE_POINT_LIST_DEFAULT_OPTIONS: ChargePointListOptionsModel = {
  columnVisibility: CHARGE_POINT_LIST_COLUMN_VISIBILITY,
  columnOrder: CHARGE_POINT_LIST_COLUMN_ORDER,
};

export const CHARGE_POINT_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  isOnline: 'delmonicos-shared-sdk.outputs-charge-point.is-online',
  identity: 'delmonicos-shared-sdk.outputs-charge-point.identity',
  name: 'delmonicos-shared-sdk.outputs-charge-point.name',
  location: 'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
  locationCity: 'delmonicos-shared-sdk.outputs-location.city',
  model: 'delmonicos-shared-sdk.outputs-charge-point.model',
  network: 'delmonicos-shared-sdk.outputs-gateway-charge-point.network',
  customer: 'delmonicos-shared-sdk.outputs-gateway-charge-point.customer',
  tariff: 'delmonicos-shared-sdk.outputs-gateway-charge-point.tariff',
  channels: 'delmonicos-shared-sdk.outputs-charge-point-alt.channels',
  isLocked: 'delmonicos-shared-sdk.outputs-charge-point.is-locked',
  createdAt: 'delmonicos-shared-sdk.outputs-base-with-timestamp.created-at',
  lastRecordedActivityDate:
    'delmonicos-shared-sdk.outputs-charge-point.last-recorded-activity-date',
};
